import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail, STATIC_MODE, IMAGES } from "@/lib/site";
import { Lock, AlertTriangle } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-royal-dark flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-gold/15 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-royal/30 blur-3xl" />
      <div className="relative bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-black ring-2 ring-gold/60 overflow-hidden flex items-center justify-center">
            <img src={IMAGES.logo} alt="Royal English Medium High School" className="w-full h-full object-contain p-1" />
          </div>
          <div>
            <h1 className="font-serif-display text-xl font-bold text-royal">ROYAL ENGLISH</h1>
            <p className="text-[10px] text-gold font-semibold tracking-[0.2em]">MEDIUM HIGH SCHOOL</p>
          </div>
        </Link>

        <div className="flex items-center gap-2 text-gold mb-2">
          <Lock size={18} />
          <span className="text-xs font-bold tracking-[0.25em]">ADMIN PORTAL</span>
        </div>
        <h2 className="font-serif-display text-3xl font-bold text-royal">Sign In</h2>
        <p className="text-sm text-gray-500 mt-1">Access the school management dashboard.</p>

        {STATIC_MODE && (
          <div data-testid="static-warning" className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>This is the static build of the site. The Admin Dashboard requires the backend to be deployed (Render / Railway / Emergent). Login will not work here.</span>
          </div>
        )}

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div>
            <Label className="font-semibold">Email</Label>
            <Input data-testid="login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label className="font-semibold">Password</Label>
            <Input data-testid="login-password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" />
          </div>
          <Button data-testid="login-submit" disabled={loading} type="submit"
            className="w-full h-12 bg-royal hover:bg-gold hover:text-royal-dark text-white font-bold rounded-full transition-all">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <Link to="/" className="block text-center text-sm text-gray-500 mt-6 hover:text-royal">← Back to website</Link>
      </div>
    </div>
  );
}
